/**
 * People advanced-filter document — a versioned value the web filter panel
 * (`PeopleFilterPanel`) edits and evaluates CLIENT-SIDE over the complete
 * person index. One schema + ONE evaluator, both in `@releve/shared` (the
 * projects/businesses template on `filterDocumentPrimitives.ts`).
 *
 * The document is AD HOC (component state for the session) and has NO seeded
 * default — `EMPTY_PERSON_FILTER_DOCUMENT` is the only starting value.
 *
 * ── v1 vocabulary ───────────────────────────────────────────────────────────
 *  - `person.status`         string family over the raw status token (NULL
 *                            reads as `''`, reachable through `is_empty`).
 *  - `person.country`        string family.
 *  - `person.city`           string family.
 *  - `person.person_type`    ARRAY family over the raw `person_type` tokens
 *                            (a NULL column reads as `[]`).
 *  - `person.skill_id`       ARRAY family over `skill_ids`.
 *  - `person.discipline_id`  ARRAY family over `discipline_ids`.
 *  - `person.business_id`    ARRAY family over `business_ids` (the panel
 *                            labels options from the paired `business_names`).
 * `array_has_any` matches a person carrying ANY selected value;
 * `array_is_empty` matches a person with none.
 *
 * ── Interactions (20260928170000, appended under the same `version: 1` —
 *    documents are session-only, so none can predate them) ─────────────────
 *  - `person.interaction_project_id` ARRAY family over
 *                            `interaction_project_ids` ("met during"; labels
 *                            from the paired `interaction_project_names`).
 *  - `person.interaction_type_id`    ARRAY family over `interaction_type_ids`
 *                            (labels from the paired `interaction_type_codes`).
 *  - `person.last_contact_on`        DATE family over the calendar day
 *                            `last_contact_on` (never met = matches no range).
 *  - `person.open_follow_up`         STRING family over `open_follow_up`
 *                            (`'open'` | `''`): "has an open follow-up" is
 *                            `{op:'in', values:['open']}`; "has none" is
 *                            `{op:'is_empty'}`.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b the grid runs the panel only — ZERO
 * filterable columns. Fields are `person.<field>` over the MAPPED rows (the
 * raw wire row satisfies the same shape).
 *
 * `countActivePersonConditions` carries the entity in its name because the
 * bare `countActiveConditions` is already exported from the barrel.
 */
import { z } from "zod";
import { makeArrayConditionSchema, makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesArrayOp, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const PERSON_FILTER_STRING_FIELDS = [
    "person.status",
    "person.country",
    "person.city",
    "person.open_follow_up",
];
export const PERSON_FILTER_ARRAY_FIELDS = [
    "person.person_type",
    "person.skill_id",
    "person.discipline_id",
    "person.business_id",
    "person.interaction_project_id",
    "person.interaction_type_id",
];
export const PERSON_FILTER_DATE_FIELDS = ["person.last_contact_on"];
/** The one value `person.open_follow_up` is filtered `in` (`person_index.open_follow_up`). */
export const PERSON_OPEN_FOLLOW_UP_VALUE = "open";
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * Three families with DISJOINT op sets ('in'/'is_empty' vs
 * 'array_has_any'/'array_is_empty' vs the three date ops), so
 * `matchesCondition` narrows on `op` alone. "Nothing checked" is NO condition — an `in` / `array_has_any` with
 * zero values is rejected by the schema.
 */
const stringConditionSchema = makeStringConditionSchema(PERSON_FILTER_STRING_FIELDS);
const arrayConditionSchema = makeArrayConditionSchema(PERSON_FILTER_ARRAY_FIELDS);
const dateConditionSchema = makeDateConditionSchema(PERSON_FILTER_DATE_FIELDS);
export const personFilterConditionSchema = z.union([
    stringConditionSchema,
    arrayConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const personFilterDocumentSchema = makeFilterDocumentSchema(personFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_PERSON_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch with NO default:
 * a new vocabulary entry is a TS2366 until it is handled here.
 */
function stringValue(row, field) {
    switch (field) {
        case "person.status":
            return row.status ?? "";
        case "person.country":
            return row.country ?? "";
        case "person.city":
            return row.city ?? "";
        case "person.open_follow_up":
            return row.open_follow_up ?? "";
    }
}
function arrayValue(row, field) {
    switch (field) {
        case "person.person_type":
            return [...(row.person_type ?? [])];
        case "person.skill_id":
            return [...row.skill_ids];
        case "person.discipline_id":
            return [...row.discipline_ids];
        case "person.business_id":
            return [...row.business_ids];
        case "person.interaction_project_id":
            return [...row.interaction_project_ids];
        case "person.interaction_type_id":
            return [...row.interaction_type_ids];
    }
}
/** `last_contact_on` is already a calendar day — `toLocalCalendarDay` returns it verbatim. */
function dateValue(row, field) {
    switch (field) {
        case "person.last_contact_on":
            return toLocalCalendarDay(row.last_contact_on);
    }
}
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "array_has_any":
        case "array_is_empty":
            return matchesArrayOp(c.op, arrayValue(row, c.field), c.values);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to person rows (AND of all conditions — v1 `match`
 * is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyPersonFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActivePersonConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=peopleFilters.js.map